import ApiErrorMixin from '@/mixins/ApiErrorMixin'

/** Verde livre, laranja em atendimento, cinza quando a leitura envelheceu. */
const FLEET_COLORS = { free: '#28c76f', busy: '#ff9f43', stale: '#b4bdc6' }

/**
 * Ultimas posicoes das viaturas do no, para o mapa.
 *
 * Duas fontes que se completam: a carga inicial pelo GET /posicoes, porque
 * quem abre a tela nao pode esperar ate 30s pela proxima leva; e o canal
 * `node.{id}`, que a partir dai atualiza sozinho.
 */
export default {
  mixins: [ApiErrorMixin],

  data() {
    return {
      fleetPositions: [],
      fleetFetchedAt: null,
      fleetStaleAfter: 180,
      loadingFleetPositions: false,
      fleetChannelName: null,
      fleetListener: null,
      fleetStaleTimer: null,
    }
  },

  computed: {
    /** Só o que dá para desenhar: sem coordenada não há pino. */
    fleetPositionsOnMap() {
      return this.fleetPositions.filter(p => p.lat !== null && p.lng !== null)
    },
  },

  methods: {
    async loadFleetPositions() {
      this.loadingFleetPositions = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}posicoes`)
        this.fleetPositions = res.data.positions || []
        this.fleetFetchedAt = res.data.fetched_at || null
        this.fleetStaleAfter = res.data.stale_after_seconds || 180
      } catch (e) {
        this.fleetPositions = []
        this.$message('Erro', this.apiErrorText(e, 'Não foi possível carregar as posições das viaturas.'), 'error')
      } finally {
        this.loadingFleetPositions = false
      }
    },

    watchFleetPositions() {
      if (typeof window.Echo === 'undefined') return
      if (this.fleetListener) return

      const node = this.$store.state.selected_node
      if (!node || !node.id) return

      this.fleetChannelName = `node.${node.id}`
      this.fleetListener = payload => this.onFleetPositions(payload)

      window.Echo.private(this.fleetChannelName)
        .listen('.VehiclePositions', this.fleetListener)

      // O servidor decide o `stale` na leitura, mas uma tela aberta parada
      // precisa envelhecer sozinha.
      this.fleetStaleTimer = setInterval(() => this.refreshFleetStale(), 30000)
    },

    unwatchFleetPositions() {
      if (this.fleetStaleTimer) {
        clearInterval(this.fleetStaleTimer)
        this.fleetStaleTimer = null
      }

      if (typeof window.Echo === 'undefined' || !this.fleetChannelName) return

      // stopListening e nao leave(): o TeamRequestsMixin assina o mesmo canal,
      // e sair dele aqui derrubaria o alerta de solicitação de equipe junto.
      window.Echo.private(this.fleetChannelName)
        .stopListening('.VehiclePositions', this.fleetListener)

      this.fleetChannelName = null
      this.fleetListener = null
    },

    onFleetPositions(payload) {
      const chegando = payload && payload.positions
      if (!chegando) return

      Object.keys(chegando).forEach(code => {
        const nova = chegando[code]
        const i = this.fleetPositions.findIndex(p => p.identifier_code === code)

        const registro = {
          identifier_code: code,
          lat: nova.lat,
          lng: nova.lng,
          speed: nova.speed,
          ignition: nova.ignition,
          at: nova.at,
          age_seconds: 0,
          stale: false,
          // O broadcast não carrega a equipe; conserva a que veio da carga
          // inicial em vez de apagar o nome do pino a cada leva.
          team: i >= 0 ? this.fleetPositions[i].team : null,
        }

        if (i >= 0) this.$set(this.fleetPositions, i, registro)
        else this.fleetPositions.push(registro)
      })

      if (typeof this.onFleetPositionsApplied === 'function') {
        this.onFleetPositionsApplied(Object.keys(chegando))
      }
    },

    refreshFleetStale() {
      const agora = Date.now()

      this.fleetPositions.forEach((p, i) => {
        if (!p.at) return
        const idade = Math.floor((agora - new Date(p.at).getTime()) / 1000)
        if (idade === p.age_seconds) return

        this.$set(this.fleetPositions, i, {
          ...p,
          age_seconds: idade,
          stale: idade > this.fleetStaleAfter,
        })
      })
    },

    fleetColor(position) {
      if (position.stale) return FLEET_COLORS.stale
      return position.team && position.team.busy ? FLEET_COLORS.busy : FLEET_COLORS.free
    },

    fleetStateLabel(position) {
      if (!position.team) return 'código sem equipe'
      return position.team.busy ? 'em atendimento' : 'livre'
    },

    /**
     * Pino desenhado inline: nada para carregar, e a cor acompanha o estado sem
     * um arquivo por combinação. Destacado cresce em vez de mudar de cor — a cor
     * já significa outra coisa aqui.
     */
    fleetMarkerIcon(position, highlighted = false, outOfRadius = false) {
      const cor = this.fleetColor(position)
      const w = highlighted ? 38 : 28
      const h = highlighted ? 52 : 38
      // Fora do raio esmaece em vez de sumir: o despachador precisa saber que
      // existe uma viatura logo depois da linha antes de descartá-la.
      const opacidade = outOfRadius ? 0.35 : 1
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 28 38">`
        + `<g opacity="${opacidade}">`
        + `<path d="M14 0C6.3 0 0 6.3 0 14c0 10 14 24 14 24s14-14 14-24C28 6.3 21.7 0 14 0z" `
        + `fill="${cor}" stroke="#ffffff" stroke-width="2"/>`
        + `<circle cx="14" cy="14" r="5" fill="#ffffff"/></g></svg>`

      return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}` }
    },

    fleetMarkerTitle(position) {
      const equipe = position.team ? position.team.name : 'sem equipe vinculada'
      return `${position.identifier_code} · ${equipe} · ${this.fleetStateLabel(position)} · ${this.fleetAgeLabel(position)}`
    },

    /**
     * Distância em linha reta, em km. É o que desenha o raio: instantânea e de
     * graça. A distância por rota vem do Google e custa cota — as duas sempre
     * divergem, e a tela precisa dizer qual está mostrando.
     */
    fleetStraightLineKm(lat1, lng1, lat2, lng2) {
      const dLat = (lat2 - lat1) * 111.32
      const dLng = (lng2 - lng1) * 111.32 * Math.cos(((lat1 + lat2) / 2) * (Math.PI / 180))
      return Math.sqrt(dLat * dLat + dLng * dLng)
    },

    /** "há 4 min" — só aparece no pino quando a leitura já envelheceu. */
    fleetAgeLabel(position) {
      const s = position.age_seconds
      if (s === null || s === undefined) return 'sem horário'
      if (s < 60) return `há ${s}s`
      return `há ${Math.floor(s / 60)} min`
    },
  },

  beforeDestroy() {
    this.unwatchFleetPositions()
  },
}
